/**
 * Facility View helpers for spirits containers on DSP sites.
 * Separate from brewery equipment / batches — never maps containers into equipment.
 */

export const FACILITY_SPIRITS_DSP_SITES = ['DSP-AL-20010', 'DSP-AL-20051'] as const;

export type FacilitySpiritContainer = {
  id: number;
  siteId: string;
  containerSerial: string;
  containerType?: string;
  kind?: string | null;
  proof?: number | null;
  status?: string | null;
  capacityGal?: number | null;
  remainingProofGallons?: number | null;
  remainingWineGallons?: number | null;
  producerPlantNumber?: string | null;
};

/** Destroyed / closed / voided — terminal; history only, never refill. */
export function isSpiritContainerDestroyed(
  c: Pick<FacilitySpiritContainer, 'status' | 'remainingProofGallons'>
): boolean {
  const status = String(c.status || '').trim();
  if (status === 'Destroyed' || status === 'Closed' || status === 'Voided') return true;
  const pg = Number(c.remainingProofGallons);
  return !Number.isFinite(pg) || pg <= 0.05;
}

/** Empty ≈ destroyed/closed/voided, or essentially no PG left (mirrors listEligibleSources). */
export function isSpiritContainerEmpty(c: Pick<FacilitySpiritContainer, 'status' | 'remainingProofGallons'>): boolean {
  return isSpiritContainerDestroyed(c);
}

export function isFacilitySpiritsSite(siteId: string | null | undefined): boolean {
  return FACILITY_SPIRITS_DSP_SITES.includes(String(siteId || '').trim() as (typeof FACILITY_SPIRITS_DSP_SITES)[number]);
}

/** Full PG for the fill bar: capacity × proof / 100 (Part 30), fallback to remaining. */
export function spiritContainerFullProofGallons(
  c: Pick<FacilitySpiritContainer, 'capacityGal' | 'proof' | 'remainingProofGallons'>
): number {
  const capacity = Number(c.capacityGal);
  const proof = Number(c.proof);
  if (Number.isFinite(capacity) && capacity > 0 && Number.isFinite(proof) && proof > 0) {
    return (capacity * proof) / 100;
  }
  const rem = Number(c.remainingProofGallons);
  return Number.isFinite(rem) && rem > 0 ? rem : 0;
}

/** 0–100 fill percent for the remaining bar. */
export function spiritContainerFillPercent(
  c: Pick<FacilitySpiritContainer, 'capacityGal' | 'proof' | 'remainingProofGallons'>
): number {
  const full = spiritContainerFullProofGallons(c);
  const rem = Number(c.remainingProofGallons);
  if (!Number.isFinite(full) || full <= 0 || !Number.isFinite(rem) || rem <= 0) return 0;
  return Math.max(0, Math.min(100, (rem / full) * 100));
}

export function formatSpiritProofGallons(pg: number | null | undefined): string {
  const n = Number(pg);
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(2);
}

/** Group containers by kind (stable A→Z), then by serial. */
export function groupSpiritContainersByKind<T extends FacilitySpiritContainer>(containers: T[]): { kind: string; containers: T[] }[] {
  const map = new Map<string, T[]>();
  for (const c of containers) {
    const kind = String(c.kind || 'Unknown').trim() || 'Unknown';
    const list = map.get(kind) || [];
    list.push(c);
    map.set(kind, list);
  }
  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([kind, list]) => ({
      kind,
      containers: list.slice().sort((a, b) =>
        String(a.containerSerial || '').localeCompare(String(b.containerSerial || ''))
      ),
    }));
}

export function filterFacilitySpiritContainers(
  containers: FacilitySpiritContainer[],
  { showEmpty }: { showEmpty: boolean }
): FacilitySpiritContainer[] {
  return (containers || []).filter((c) => showEmpty || !isSpiritContainerEmpty(c));
}

/** Deep-link into SpiritsDashboard bottle / transfer / history with a container preselected. */
export function spiritsDeepLink(opts: {
  siteId: string;
  mode: 'bottle' | 'transfer' | 'history';
  containerId: number;
}): string {
  const q = new URLSearchParams({
    siteId: opts.siteId,
    mode: opts.mode,
    containerId: String(opts.containerId),
  });
  return `/spirits?${q.toString()}`;
}

export type SpiritProductLike = {
  id: number;
  code?: string;
  name?: string;
  kind?: string | null;
  targetProof?: number | null;
  sourceCategory?: string | null;
};

/**
 * Map a bulk container kind + proof to spirit_products.sourceCategory values.
 * Mirrors 19.593 tank-summary vs 19.592 container-record split (neutral/cane ≥190
 * uses tank summary; rum and other kinds stay on per-container records).
 */
export function sourceCategoriesForContainer(
  kind: string | null | undefined,
  proof: number | null | undefined
): string[] {
  const k = String(kind || '')
    .trim()
    .toLowerCase();
  const p = Number(proof);
  if (!k) return [];

  if (k.includes('neutral') || k.includes('cane') || k === 'ngs' || k.includes('vodka')) {
    return Number.isFinite(p) && p >= 190 ? ['Neutral 190 and Above'] : ['Neutral 190 and Below'];
  }
  if (k.includes('rum')) return ['Rum'];
  if (k.includes('gin')) return ['Gin'];
  if (k.includes('agave')) return ['Agave Spirit'];
  if (k.includes('bourbon')) return ['Whiskey'];
  if (k.includes('whiskey') || k.includes('whisky')) {
    // Aged light whiskey often bottles as whiskey; seed also ties WHISKEY to "Neutral 190 and Below".
    return ['Whiskey', 'Neutral 190 and Below'];
  }
  // Exact kind / category fallback (e.g. Moonshine container → Moonshine product).
  return [String(kind).trim()];
}

/** Products that can be bottled from this bulk container (by sourceCategory / kind + proof). */
export function productsCompatibleWithContainer<T extends SpiritProductLike>(
  container: Pick<FacilitySpiritContainer, 'kind' | 'proof'>,
  products: T[]
): T[] {
  const cats = sourceCategoriesForContainer(container.kind, container.proof).map((c) =>
    c.toLowerCase()
  );
  const kindLower = String(container.kind || '')
    .trim()
    .toLowerCase();
  const proof = Number(container.proof);
  return (products || []).filter((p) => {
    const target = Number(p.targetProof);
    if (Number.isFinite(proof) && Number.isFinite(target) && target > proof + 0.001) return false;
    const cat = String(p.sourceCategory || '')
      .trim()
      .toLowerCase();
    const pKind = String(p.kind || '')
      .trim()
      .toLowerCase();
    if (cat && cats.includes(cat)) return true;
    if (kindLower && pKind && pKind === kindLower) return true;
    if (kindLower && cats.includes(pKind)) return true;
    return false;
  });
}

/** Unique product id when exactly one product matches; otherwise null (user must choose). */
export function uniqueCompatibleProductId(
  container: Pick<FacilitySpiritContainer, 'kind' | 'proof'>,
  products: SpiritProductLike[]
): number | null {
  const matches = productsCompatibleWithContainer(container, products);
  return matches.length === 1 ? Number(matches[0].id) : null;
}

export function sameSpiritId(a: number | string | null | undefined, b: number | string | null | undefined): boolean {
  const na = Number(a);
  const nb = Number(b);
  return Number.isFinite(na) && Number.isFinite(nb) && na === nb;
}
